import React, { useEffect, useCallback } from 'react';
import { useTutorial } from '../../contexts/TutorialContext';
import { TutorialSpotlight } from './TutorialSpotlight';
import { TOTAL_TUTORIAL_PAGES } from '../../types/tutorial';

// Import tutorial pages
import { WelcomePage } from './pages/WelcomePage';
import { CategoryPage } from './pages/CategoryPage';
import { CluesPage } from './pages/CluesPage';
import { GuessPage } from './pages/GuessPage';
import { SharePage } from './pages/SharePage';

export interface TutorialOverlayProps {
  className?: string;
}

/**
 * Custom smooth scroll with easeOutCubic easing for butter-smooth animation
 * More consistent than browser's scrollIntoView behavior
 */
function smoothScrollToElement(element: Element, duration = 400) {
  const elementRect = element.getBoundingClientRect();
  const elementCenter = elementRect.top + elementRect.height / 2;
  const viewportCenter = window.innerHeight / 2;
  const targetY = window.scrollY + elementCenter - viewportCenter;
  const startY = window.scrollY;
  const diff = targetY - startY;

  // No need to scroll if already in view
  if (Math.abs(diff) < 10) return;

  const startTime = performance.now();

  // easeOutCubic: smooth deceleration
  const easeOutCubic = (t: number) => 1 - Math.pow(1 - t, 3);

  function step(currentTime: number) {
    const elapsed = currentTime - startTime;
    const progress = Math.min(elapsed / duration, 1);
    const easedProgress = easeOutCubic(progress);

    window.scrollTo(0, startY + diff * easedProgress);

    if (progress < 1) {
      requestAnimationFrame(step);
    }
  }

  requestAnimationFrame(step);
}

/**
 * Main tutorial overlay component that manages the entire tutorial flow
 */
export const TutorialOverlay: React.FC<TutorialOverlayProps> = ({ className }) => {
  const {
    tutorialState,
    nextPage,
    prevPage,
    skipTutorial,
    completeTutorial
  } = useTutorial();

  // Handle keyboard shortcuts
  useEffect(() => {
    if (!tutorialState.isActive) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        skipTutorial();
      } else if (event.key === 'Enter' || event.key === 'ArrowRight') {
        // Advance to next page or complete tutorial
        if (tutorialState.currentPage === TOTAL_TUTORIAL_PAGES) {
          completeTutorial();
        } else {
          nextPage();
        }
      } else if (event.key === 'ArrowLeft') {
        // Go to previous page (if not on first page)
        if (tutorialState.currentPage > 1) {
          prevPage();
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [tutorialState.isActive, tutorialState.currentPage, skipTutorial, nextPage, prevPage, completeTutorial]);

  // Auto-scroll to spotlighted elements when page changes
  useEffect(() => {
    if (!tutorialState.isActive) return;

    const scrollToSpotlight = () => {
      // Map page numbers to their spotlighted element IDs
      const targetIdsByPage: { [key: number]: string[] } = {
        2: ['categories-section'],
        3: ['price-chart', 'financial-metrics', 'hints-section'],
        4: ['submit-guesses']
      };

      const targetIds = targetIdsByPage[tutorialState.currentPage];

      if (targetIds && targetIds.length > 0) {
        // Find the first available spotlighted element
        const element = document.querySelector(`[data-tutorial-id="${targetIds[0]}"]`);

        if (element) {
          // Use custom smooth scroll for consistent 60fps animation
          smoothScrollToElement(element, 400);
        }
      }
    };

    // Delay to let modal animations settle before scrolling
    const timeoutId = setTimeout(scrollToSpotlight, 200);

    return () => {
      clearTimeout(timeoutId);
    };
  }, [tutorialState.isActive, tutorialState.currentPage]);

  const handleNext = useCallback(() => {
    if (tutorialState.currentPage === TOTAL_TUTORIAL_PAGES) {
      completeTutorial();
    } else {
      nextPage();
    }
  }, [tutorialState.currentPage, nextPage, completeTutorial]);

  const handleFinish = useCallback(() => {
    completeTutorial();
  }, [completeTutorial]);

  if (!tutorialState.isActive) {
    return null;
  }

  // Render appropriate page based on current page number
  const renderPage = () => {
    const commonProps = {
      currentPage: tutorialState.currentPage,
      totalPages: TOTAL_TUTORIAL_PAGES,
      onNext: handleNext,
      onPrevious: prevPage,
      onSkip: skipTutorial,
      onFinish: handleFinish
    };

    switch (tutorialState.currentPage) {
      case 1:
        return <WelcomePage {...commonProps} />;
      case 2:
        return (
          <>
            <TutorialSpotlight targetIds={['categories-section']} />
            <CategoryPage {...commonProps} />
          </>
        );
      case 3:
        return (
          <>
            <TutorialSpotlight
              targetIds={['price-chart', 'financial-metrics', 'hints-section']}
            />
            <CluesPage {...commonProps} />
          </>
        );
      case 4:
        return (
          <>
            <TutorialSpotlight targetIds={['submit-guesses']} />
            <GuessPage {...commonProps} />
          </>
        );
      case 5:
        return <SharePage {...commonProps} />;
      default:
        return null;
    }
  };

  return (
    <div className={className}>
      {/* Backdrop blur - click to advance */}
      <div
        className="fixed inset-0 z-[9997] transition-opacity duration-300 cursor-pointer"
        aria-hidden="true"
        onClick={handleNext}
      />

      {/* Tutorial content */}
      {renderPage()}
    </div>
  );
};

export default TutorialOverlay;
